
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useCampaignRules } from "@/hooks/campaigns/useCampaignRules";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface CampaignRulesPreviewProps {
  campaignId: string;
}

const CampaignRulesPreview = ({ campaignId }: CampaignRulesPreviewProps) => {
  const { data: rules = [], isLoading } = useCampaignRules(campaignId);

  const getRuleLabel = (ruleType: string) => {
    const labels: Record<string, string> = {
      cashback_percentage: 'Percentual de Cashback',
      expiration_days: 'Dias de Expiração',
      referrer_bonus_value: 'Bônus do Indicador',
      referred_discount_value: 'Desconto do Indicado',
    };
    return labels[ruleType] || ruleType;
  };

  const formatValue = (value: number, ruleType: string) => {
    if (ruleType.includes('percentage')) {
      return `${value}%`;
    }
    if (ruleType.includes('days')) {
      return `${value} dias`;
    }
    if (ruleType.includes('value') || ruleType.includes('bonus')) {
      return `R$ ${value.toFixed(2)}`;
    }
    return value.toString();
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Regras da Campanha</CardTitle>
          <CardDescription>Configurações aplicadas durante a campanha</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">Carregando...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Regras da Campanha</CardTitle>
        <CardDescription>Configurações aplicadas durante a campanha</CardDescription>
      </CardHeader>
      <CardContent>
        {rules.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground">
            Nenhuma regra configurada
          </div>
        ) : (
          <div className="space-y-4">
            {rules.map((rule) => (
              <div key={rule.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex-1">
                  <p className="font-medium">{getRuleLabel(rule.rule_type)}</p>
                  <div className="flex items-center space-x-2 mt-1">
                    {rule.original_value && (
                      <span className="text-sm text-muted-foreground line-through">
                        {formatValue(rule.original_value, rule.rule_type)}
                      </span>
                    )}
                    <span className="text-sm font-medium text-green-600">
                      {formatValue(rule.campaign_value, rule.rule_type)}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col items-end space-y-1">
                  {rule.applied_at && (
                    <Badge variant="default" className="text-xs">
                      Aplicada
                    </Badge>
                  )}
                  {rule.reverted_at && (
                    <Badge variant="secondary" className="text-xs">
                      Revertida
                    </Badge>
                  )}
                  {!rule.applied_at && !rule.reverted_at && (
                    <Badge variant="outline" className="text-xs">
                      Pendente
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CampaignRulesPreview;
